import React, { useState } from "react";
import {
  Box,
  Text,
  ScrollArea,
  Group,
  Card,
  Stack,
  ActionIcon,
  Loader,
  Drawer,
  useMantineTheme,
  useMantineColorScheme,
  Menu,
  Checkbox,
  SegmentedControl,
} from "@mantine/core";
import { 
  BellIcon as Bell, 
  DotsThreeVertical,
  Trash,
} from "@phosphor-icons/react";
import { useMediaQuery } from "@mantine/hooks";
import { notifications as mantineNotifications } from "@mantine/notifications";
import { useNotifications, useUpdateNotification, useDeleteNotification } from "@/api";
import { useNotificationContext } from "./notification-context";
import { useEffect } from "react";

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; 
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
  
  return `${month} ${day}, ${year} ${hours}:${minutesStr} ${ampm}`;
};

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { setUnreadCount } = useNotificationContext();
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [filter, setFilter] = useState<"all" | "unread" | "read">("all");
  const [selectedNotifications, setSelectedNotifications] = useState<Set<number>>(new Set());
  const [deletingNotification, setDeletingNotification] = useState<number | null>(null);

  // Handle escape key to close panel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  const {
    data: notificationsData,
    isLoading,
    refetch,
  } = useNotifications({
    page_size: 50,
  }, {query: {queryKey: ["notifications"]}});

  const { mutateAsync: updateNotification } = useUpdateNotification();
  const { mutateAsync: deleteNotification } = useDeleteNotification();
  const [markingAsRead, setMarkingAsRead] = useState<number | null>(null);

  // Sync unread count with API data
  useEffect(() => {
    if (notificationsData?.results) {
      const unreadCount = notificationsData.results.filter((n) => !n.is_read).length;
      setUnreadCount(unreadCount);
    }
  }, [notificationsData, setUnreadCount]);

  const handleMarkAllAsRead = async () => {
    if (!notificationsData?.results) return;

    const unreadNotifications = notificationsData.results.filter((n) => !n.is_read);

    if (unreadNotifications.length === 0) return;

    try {
      setMarkingAllAsRead(true);
      
      // Update all unread notifications
      await Promise.all(
        unreadNotifications.map(notification =>
          updateNotification({
            id: notification.id,
            data: { is_read: true }
          })
        )
      );
      
      mantineNotifications.show({
        title: "Success",
        message: `${unreadNotifications.length} notifications marked as read`,
        color: "green",
      });
      refetch();
    } catch (error) {
      mantineNotifications.show({
        title: "Error",
        message: "Failed to mark all notifications as read",
        color: "red",
      });
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const unreadCount = notificationsData?.results?.filter((n) => !n.is_read).length || 0;
  const readCount = notificationsData?.results?.filter((n) => n.is_read).length || 0;
  const totalCount = notificationsData?.results?.length || 0;

  const filteredNotifications = notificationsData?.results?.filter((n) => {
    if (filter === "unread") return !n.is_read;
    if (filter === "read") return n.is_read;
    return true;
  }) || [];

  const handleToggleSelect = (notificationId: number) => {
    setSelectedNotifications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const handleDeleteNotification = async (notificationId: number) => {
    try {
      setDeletingNotification(notificationId);
      await deleteNotification({ id: notificationId });
      mantineNotifications.show({
        title: "Success",
        message: "Notification deleted",
        color: "green",
      });
      refetch();
      setSelectedNotifications((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    } catch (error) {
      mantineNotifications.show({
        title: "Error",
        message: "Failed to delete notification",
        color: "red",
      });
    } finally {
      setDeletingNotification(null);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      setMarkingAsRead(notificationId);
      await updateNotification({
        id: notificationId,
        data: { is_read: true }
      });
      refetch();
    } catch (error) {
      mantineNotifications.show({
        title: "Error",
        message: "Failed to mark notification as read",
        color: "red",
      });
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNotifications.size === 0) return;
    
    try {
      await Promise.all(
        Array.from(selectedNotifications).map((id) => deleteNotification({ id }))
      );
      mantineNotifications.show({
        title: "Success",
        message: `${selectedNotifications.size} notification(s) deleted`,
        color: "green",
      });
      refetch();
      setSelectedNotifications(new Set());
    } catch (error) {
      mantineNotifications.show({
        title: "Error",
        message: "Failed to delete notifications",
        color: "red",
      });
    }
  };

  const notificationContent = (
    <Box
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: colorScheme === "dark" ? theme.colors.dark[8] : "white",
      }}
    >
      <Box p="md" style={{ borderBottom: `1px solid ${colorScheme === "dark" ? theme.colors.dark[6] : "#e0e0e0"}` }}>
        <Stack gap="md">
          {/* Title and Menu Button */}
          <Group justify="space-between" align="center">
            <Text fw={700} size="lg" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
              Notifications
            </Text>
            <Menu shadow="md" width={200} position="bottom" offset={5}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="md">
                  <DotsThreeVertical size={20} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={handleMarkAllAsRead}
                  disabled={unreadCount === 0 || markingAllAsRead}
                >
                  Mark all as Read
                </Menu.Item>
                <Menu.Item
                  onClick={handleDeleteSelected}
                  disabled={selectedNotifications.size === 0}
                >
                  Delete
                </Menu.Item>
                <Menu.Item>
                  Cancel
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
          
          {/* Filter Buttons */}
          <Group justify="space-between" align="center" wrap="nowrap">
            <SegmentedControl
              value={filter}
              onChange={(value) => {
                setFilter(value as "all" | "unread" | "read");
                setSelectedNotifications(new Set());
              }}
              data={[
                { 
                  label: (
                    <Group gap={6} wrap="nowrap">
                      <Text 
                        size="sm" 
                        fw={500}
                        c={filter === "all" ? "white" : colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}
                      >
                        All
                      </Text>
                      <Box
                        style={{
                          backgroundColor: filter === "all" ? "rgba(255, 255, 255, 0.3)" : "#E5E7EB",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          minWidth: "24px",
                          textAlign: "center",
                        }}
                      >
                        <Text 
                          size="xs" 
                          fw={600}
                          c={filter === "all" ? "white" : colorScheme === "dark" ? theme.colors.neutral[7] : theme.colors.neutral[9]}
                        >
                          {totalCount}
                        </Text>
                      </Box>
                    </Group>
                  ), 
                  value: "all" 
                },
                { 
                  label: (
                    <Group gap={6} wrap="nowrap">
                      <Text 
                        size="sm" 
                        fw={500}
                        c={filter === "unread" ? "white" : colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}
                      >
                        Unread
                      </Text>
                      <Box
                        style={{
                          backgroundColor: filter === "unread" ? "rgba(255, 255, 255, 0.3)" : "#E5E7EB",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          minWidth: "24px",
                          textAlign: "center",
                        }}
                      >
                        <Text 
                          size="xs" 
                          fw={600}
                          c={filter === "unread" ? "white" : colorScheme === "dark" ? theme.colors.neutral[7] : theme.colors.neutral[9]}
                        >
                          {unreadCount}
                        </Text>
                      </Box>
                    </Group>
                  ), 
                  value: "unread" 
                },
                { 
                  label: (
                    <Group gap={6} wrap="nowrap">
                      <Text 
                        size="sm" 
                        fw={500}
                        c={filter === "read" ? "white" : colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}
                      >
                        Read
                      </Text>
                      <Box
                        style={{
                          backgroundColor: filter === "read" ? "rgba(255, 255, 255, 0.3)" : "#E5E7EB",
                          borderRadius: "12px",
                          padding: "2px 8px",
                          minWidth: "24px",
                          textAlign: "center",
                        }}
                      >
                        <Text 
                          size="xs" 
                          fw={600}
                          c={filter === "read" ? "white" : colorScheme === "dark" ? theme.colors.neutral[7] : theme.colors.neutral[9]}
                        >
                          {readCount}
                        </Text>
                      </Box>
                    </Group>
                  ), 
                  value: "read" 
                },
              ]}
              size="sm"
              styles={{
                indicator: {
                  backgroundColor: "#124B8C",
                },
              }}
            />
            
            <Text
              size="sm"
              c="#124B8C"
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (selectedNotifications.size === filteredNotifications.length) {
                  setSelectedNotifications(new Set());
                } else {
                  setSelectedNotifications(new Set(filteredNotifications.map((n) => n.id)));
                }
              }}
              fw={500}
            >
              {selectedNotifications.size === filteredNotifications.length && filteredNotifications.length > 0
                ? "Unselect"
                : "Select All"}
            </Text>
          </Group>
        </Stack>
      </Box>

      {isLoading ? (
        <Box style={{ padding: "20px", textAlign: "center" }}>
          <Loader size="md" />
          <Text mt="sm" c="dimmed">
            Loading notifications...
          </Text>
        </Box>
      ) : !filteredNotifications.length ? (
        <Box style={{ padding: "20px", textAlign: "center" }}>
          <Bell size={48} style={{ opacity: 0.3 }} />
          <Text mt="sm" c="dimmed">
            No notifications yet
          </Text>
        </Box>
      ) : (
        <ScrollArea style={{ flex: 1 }}>
          <Stack gap="xs" p="md">
            {filteredNotifications.map((notification) => {
              const isSelected = selectedNotifications.has(notification.id);
              return (
                <Card
                  key={notification.id}
                  p="md"
                  radius="md"
                  style={{
                    border: `1px solid ${colorScheme === "dark" ? theme.colors.dark[6] : "#e0e0e0"}`,
                    backgroundColor: colorScheme === "dark" ? theme.colors.dark[7] : "white",
                    cursor: "pointer",
                  }}
                  onClick={() => handleToggleSelect(notification.id)}
                >
                  <Stack gap="xs">
                    <Group justify="space-between" align="center" wrap="nowrap" gap="md">
                      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        <Box
                          component="img"
                          src={notification.is_read ? "/assets/completed.png" : "/assets/not-started-logo.png"}
                          alt={notification.is_read ? "Completed" : "Not Started"}
                          style={{
                            width: 24,
                            height: 24,
                            flexShrink: 0,
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <Text 
                          fw={600} 
                          size="sm" 
                          c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}
                          style={{ flex: 1, minWidth: 0 }}
                          lineClamp={1}
                        >
                          {notification.title}
                        </Text>
                        <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                          {formatTimeAgo(notification.created_at)}
                        </Text>
                      </Group>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleSelect(notification.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Group>
                    
                    {/* Notification Message */}
                    <Text size="sm" c={colorScheme === "dark" ? theme.colors.neutral[2] : theme.colors.neutral[7]} lineClamp={2}>
                      {notification.message}
                    </Text>
                    
                    {/* Action Buttons */}
                    <Group justify="space-between" align="center" wrap="nowrap">
                      {!notification.is_read && (
                        <Text
                          size="sm"
                          c="blue"
                          style={{ 
                            cursor: markingAsRead === notification.id ? "wait" : "pointer",
                            opacity: markingAsRead === notification.id ? 0.6 : 1
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          fw={500}
                        >
                          {markingAsRead === notification.id ? "Marking..." : "Mark as Read"}
                        </Text>
                      )}
                      {notification.is_read && <Box />}
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="red"
                        loading={deletingNotification === notification.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification.id);
                        }}
                      >
                        <Trash size={16} />
                      </ActionIcon>
                    </Group>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </ScrollArea>
      )}
    </Box>
  );

  if (!isOpen) return null;

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      withCloseButton={false}
      size={isMobile ? "100%" : "480px"}
      position="right"
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
        body: {
          flex: 1,
          padding: 0,
        },
        header: {
          display: "none",
        },
      }}
    >
      {notificationContent}
    </Drawer>
  );
};

export default NotificationPanel;
